import type PollingClock from 'src/internal/revamp/polling/PollingClock';
import type {
  PollConfig,
  PollingState,
  RetryConfig,
  RetryState,
} from 'src/internal/revamp/polling/types';

export type PollingCallbacks = {
  isCurrentlyFetching: () => boolean;
  triggerRefetch: () => void;
  retryFetch: () => void;
};

export default class PollingController<TObserverKey> {
  private pollingClock: PollingClock | null;

  private callbacks: PollingCallbacks;

  private pollConfigs: Map<TObserverKey, PollConfig> = new Map();

  private effectiveInterval: number | null = null;

  private effectiveExpectedErrors: string[] = [];

  private effectiveUnsafeExpectAllErrors = false;

  private effectiveRetryConfig: RetryConfig | null = null;

  private clockUnsubscribe: (() => void) | null = null;

  private ticksSinceLastPoll = 0;

  private pollingState: PollingState = 'idle';

  private retryState: RetryState | null = null;

  constructor(pollingClock: PollingClock | null, callbacks: PollingCallbacks) {
    this.pollingClock = pollingClock;
    this.callbacks = callbacks;
  }

  public registerConfig(key: TObserverKey, config: PollConfig): void {
    this.pollConfigs.set(key, config);
    this.recalculateEffectiveConfigs();
    this.ensureClockSubscription();
  }

  public unregisterConfig(key: TObserverKey): void {
    this.pollConfigs.delete(key);
    this.recalculateEffectiveConfigs();
    this.cleanupIfNeeded();
  }

  public getEffectiveInterval(): number | null {
    return this.effectiveInterval;
  }

  public getEffectiveExpectedErrors(): string[] {
    return this.effectiveExpectedErrors;
  }

  public getEffectiveUnsafeExpectAllErrors(): boolean {
    return this.effectiveUnsafeExpectAllErrors;
  }

  public getEffectiveRetryConfig(): RetryConfig | null {
    return this.effectiveRetryConfig;
  }

  public getPollingState(): {isRetrying: boolean; isStopped: boolean} {
    return {
      isRetrying: this.pollingState === 'retrying',
      isStopped: this.pollingState === 'stopped',
    };
  }

  public handleFetchResult(result: {
    hasClientErrors: boolean;
    hasUnexpectedErrors: boolean;
  }): void {
    if (!result.hasClientErrors && !result.hasUnexpectedErrors) {
      this.handleSuccess();
      return;
    }

    if (result.hasClientErrors) {
      this.handleClientError();
      return;
    }

    if (result.hasUnexpectedErrors) {
      this.handleUnexpectedError();
    }
  }

  public resetTimer(): void {
    this.ticksSinceLastPoll = 0;
  }

  public cleanup(): void {
    if (this.clockUnsubscribe) {
      this.clockUnsubscribe();
      this.clockUnsubscribe = null;
    }
    this.pollingState = 'idle';
    this.ticksSinceLastPoll = 0;
    this.clearRetryState();
    this.pollConfigs.clear();
    this.effectiveInterval = null;
    this.effectiveExpectedErrors = [];
    this.effectiveUnsafeExpectAllErrors = false;
    this.effectiveRetryConfig = null;
  }

  private recalculateEffectiveConfigs(): void {
    if (this.pollConfigs.size === 0) {
      this.effectiveInterval = null;
      this.effectiveExpectedErrors = [];
      this.effectiveUnsafeExpectAllErrors = false;
      this.effectiveRetryConfig = null;
      return;
    }

    const configs = Array.from(this.pollConfigs.values());

    const intervals = configs
      .map((c) => c.interval)
      .filter((i): i is number => i !== undefined);

    this.effectiveInterval =
      intervals.length > 0 ? Math.min(...intervals) : null;

    const firstConfigErrors = configs[0].expectedErrors;

    this.effectiveExpectedErrors = firstConfigErrors.filter((error) =>
      configs.every((c) => c.expectedErrors.includes(error)),
    );
    this.effectiveUnsafeExpectAllErrors = configs.every(
      (config) => config.unsafeExpectAllErrors === true,
    );

    const retryConfigs = configs
      .map((c) => c.retryConfig)
      .filter((r): r is NonNullable<typeof r> => r !== undefined);

    if (retryConfigs.length === 0) {
      this.effectiveRetryConfig = null;
    } else {
      this.effectiveRetryConfig = {
        maxRetries: Math.max(...retryConfigs.map((r) => r.maxRetries)),
        delay: Math.min(...retryConfigs.map((r) => r.delay)),
        maxDelay: Math.min(
          ...retryConfigs.map((r) => r.maxDelay ?? Number.MAX_SAFE_INTEGER),
        ),
      };
    }
  }

  private ensureClockSubscription(): void {
    if (this.clockUnsubscribe || !this.pollingClock) {
      return;
    }

    this.clockUnsubscribe = this.pollingClock.subscribe({
      onTick: (tickTime: number) => this.handleTick(tickTime),
      onVisibilityResume: (elapsedMs: number) =>
        this.handleVisibilityResume(elapsedMs),
    });

    // Don't reset to 'polling' if retries are exhausted ('stopped') or
    // an active retry cycle is in progress ('retrying') - this preserves
    // retry state across re-subscriptions triggered by React tearing detection.
    if (this.pollingState !== 'stopped' && this.pollingState !== 'retrying') {
      this.pollingState = 'polling';
    }
  }

  private handleTick(_tickTime: number): void {
    if (this.pollingState !== 'polling') {
      return;
    }

    const interval = this.effectiveInterval;
    if (interval === null) {
      return;
    }

    this.ticksSinceLastPoll += 1000;

    if (this.ticksSinceLastPoll >= interval) {
      this.executePoll();
    }
  }

  private handleVisibilityResume(elapsedMs: number): void {
    if (this.pollingState !== 'polling') {
      return;
    }

    const interval = this.effectiveInterval;
    if (interval === null) {
      return;
    }

    if (this.ticksSinceLastPoll + elapsedMs >= interval) {
      this.executePoll();
    } else {
      this.ticksSinceLastPoll += elapsedMs;
    }
  }

  private executePoll(): void {
    if (this.callbacks.isCurrentlyFetching()) {
      return;
    }

    this.ticksSinceLastPoll = 0;
    this.callbacks.triggerRefetch();
  }

  private cleanupIfNeeded(): void {
    if (this.pollConfigs.size === 0 && this.clockUnsubscribe) {
      this.clockUnsubscribe();
      this.clockUnsubscribe = null;
      // Preserve 'stopped' and 'retrying' states across re-subscriptions.
      // 'stopped' indicates retries were exhausted and should trigger error handling.
      // 'retrying' indicates an active retry cycle that shouldn't be interrupted by
      // React re-subscriptions (e.g. useSyncExternalStore tearing detection).
      if (this.pollingState !== 'stopped' && this.pollingState !== 'retrying') {
        this.pollingState = 'idle';
        this.clearRetryState();
      }
      this.ticksSinceLastPoll = 0;
    }
  }

  private handleSuccess(): void {
    this.clearRetryState();
    if (this.pollingState === 'retrying') {
      this.pollingState = 'polling';
    }
  }

  private handleUnexpectedError(): void {
    if (!this.effectiveRetryConfig) {
      return;
    }

    // Don't start new retry cycles if retries were already exhausted
    if (this.pollingState === 'stopped') {
      return;
    }

    if (!this.retryState) {
      this.retryState = {
        attempt: 0,
        config: this.effectiveRetryConfig,
        timeoutId: null,
      };
    }

    this.retryState.attempt += 1;

    if (this.retryState.attempt > this.effectiveRetryConfig.maxRetries) {
      this.pollingState = 'stopped';
      this.clearRetryState();
      return;
    }

    this.pollingState = 'retrying';
    this.scheduleRetry();
  }

  private handleClientError(): void {
    this.pollingState = 'stopped';
    this.clearRetryState();
  }

  private scheduleRetry(): void {
    if (!this.retryState || !this.effectiveRetryConfig) {
      return;
    }

    const {delay, maxDelay} = this.effectiveRetryConfig;
    const attempt = this.retryState.attempt;
    const currentDelay = Math.min(
      delay * 1.5 ** (attempt - 1),
      maxDelay ?? Number.MAX_SAFE_INTEGER,
    );

    this.retryState.timeoutId = setTimeout(() => {
      this.executeRetry();
    }, currentDelay);
  }

  private executeRetry(): void {
    if (this.retryState) {
      this.retryState.timeoutId = null;
    }
    this.callbacks.retryFetch();
  }

  private clearRetryState(): void {
    if (this.retryState?.timeoutId) {
      clearTimeout(this.retryState.timeoutId);
    }
    this.retryState = null;
  }
}
